import React, { useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';

import { useForgotPasswordMutation } from '../../../api';
import { 
  selectForgotPasswordStatus, 
  selectAuthError,
  clearForgotPasswordStatus
} from '../authSlice';
import { useLanguage } from '../../../contexts/LanguageContext';

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const forgotPasswordStatus = useSelector(selectForgotPasswordStatus);
  const error = useSelector(selectAuthError);
  const { th } = useLanguage();
  
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  // Forgot password validation schema with translations
  const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(th('auth.emailInvalid'))
      .required(th('auth.emailRequired')),
  });

  // Clean up state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearForgotPasswordStatus());
    };
  }, [dispatch]);

  // Forgot password process
  const handleSubmit = async (values) => {
    try {
      // API'ye POST isteği gönder: { "email": "user@example.com" }
      await forgotPassword(values.email).unwrap();
      // Başarı durumu slice'da handle ediliyor
    } catch (err) {
      // Hata durumu slice'da handle ediliyor
      console.error('Şifre sıfırlama hatası:', err);
    }
  };

  return (
    <div className="surface-0 flex align-items-center justify-content-center min-h-screen">
      <div className="grid w-30rem mx-auto">
        <div className="col-12 md:col-8 lg:col-6 w-full">
          <Card className="shadow-4">
            <div className="text-center mb-5">
              <div className="text-900 text-2xl font-medium mb-3">{th('auth.forgotPasswordTitle')}</div>
              <span className="text-600 font-medium">{th('auth.forgotPasswordSubtitle')}</span>
            </div>
            
            {forgotPasswordStatus === 'success' ? (
              <div className="w-full">
                <Message 
                  severity="success" 
                  text={th('auth.resetPasswordSuccess')} 
                  className="w-full mb-4" 
                />
                <div className="flex justify-content-center mt-4">
                  <Button 
                    label={th('auth.backToLogin')} 
                    className="p-button-link" 
                    onClick={() => navigate('/login')} 
                  />
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <Message severity="error" text={error} className="w-full mb-4" />
                )}
                
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={ForgotPasswordSchema}
                  onSubmit={handleSubmit}
                >
                  {({ errors, touched }) => (
                    <Form className="p-fluid">
                      <div className="field mb-4">
                        <span className="p-input-icon-left w-full">
                          <i className="pi pi-envelope"></i>
                          <Field name="email">
                            {({ field }) => (
                              <InputText
                                id="email"
                                type="text"
                                placeholder={th('auth.email')}
                                className={`w-full text-color-secondary surface-50 border-200 ${errors.email && touched.email ? 'p-invalid' : ''}`}
                                aria-describedby="email-help"
                                {...field}
                              />
                            )}
                          </Field>
                        </span>
                        {errors.email && touched.email && (
                          <small id="email-help" className="p-error">{errors.email}</small>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 justify-content-between">
                        <Button
                          type="button"
                          label={th('auth.cancel')}
                          className="flex-auto p-button-outlined"
                          onClick={() => navigate('/login')}
                        />
                        <Button
                          type="submit"
                          label={isLoading ? th('auth.sending') : th('auth.send')}
                          className="flex-auto"
                          disabled={isLoading}
                          icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
